/**
 * WP-24: overlaps the agents could not settle between themselves. The protocol escalates them to a person; this is
 * where the person decides. One choice, which agent lands first, because that settles any overlap and is the one
 * decision a person can make without reading the thread. The coordinator enforces it like any other sequence.
 */
import type { Overlap, Resolution } from "@botallion/contracts";
import { nameOf, withNames } from "../describe.ts";
import { html, type Html } from "../html.ts";
import type { DashboardState } from "../state.ts";
import type { ViewContext } from "./common.ts";

/** Overlaps waiting on a person: escalated, and nobody has decided since. */
export function pendingDecisions(state: DashboardState): { overlap: Overlap; positions: { sessionId: string; position: string }[]; summary: string }[] {
  return Object.values(state.overlaps)
    .filter((o) => o.status === "escalated")
    .map((overlap) => {
      const escalation = state.resolutions.filter((r): r is Extract<Resolution, { outcome: "escalated" }> => r.overlapId === overlap.id && r.outcome === "escalated").at(-1);
      return { overlap, positions: escalation?.terms.positions ?? [], summary: escalation?.terms.summary ?? overlap.summary };
    })
    .sort((a, b) => a.overlap.createdAt.localeCompare(b.overlap.createdAt));
}

/** The decision cards, shown above the approvals strip whenever an agent is stuck. */
export function decisionsView(ctx: ViewContext): Html {
  const waiting = pendingDecisions(ctx.state);
  if (!waiting.length) return html``;
  const { state } = ctx;
  return html`<div class="approvals decisions">${waiting.map(({ overlap, positions, summary }) => {
    const key = `overlap:${overlap.id}`;
    const busy = ctx.busy.has(key);
    const disabled = busy || !ctx.approver.canApprove;
    // The buttons say which side (a or b); the page looks the session up, so no session id is ever in the markup.
    const button = (side: "a" | "b", first: string) =>
      html`<button class="approve" data-action="decide-overlap" data-overlap="${overlap.id}" data-pick="${side}"${disabled ? " disabled" : ""}>${busy ? "Sending..." : `${nameOf(state, first)} lands first`}</button>`;
    return html`<div class="approval decision">
      <div class="what"><strong>Two agents need you to decide:</strong> ${withNames(state, summary)}.
        ${positions.length ? html`<ul class="positions">${positions.map((p) => html`<li><strong>${nameOf(state, p.sessionId)}:</strong> ${withNames(state, p.position)}</li>`)}</ul>` : ""}
        <div class="muted">The one who lands second waits, then rebases on the first.</div></div>
      <div class="actions">${button("a", overlap.sessionA)}${button("b", overlap.sessionB)}
        ${ctx.approver.why && !busy ? html`<span class="why">${ctx.approver.why}</span>` : ""}</div>
    </div>`;
  })}</div>`;
}
